import { Grid } from '@deere/fuel-react';
import { PropTypes } from 'prop-types';
import { INTERVAL, WEEKDAYS_FULL } from '../constants';
import styles from './BidCycleCalendar.module.css';

export const BidCycleCalendar = ({
    dayToGoActive,
    daysInActivePeriod,
    daysInWithdrawalPeriod,
    interval,
}) => {
    const NUMBER_OF_WEEKS_TO_DISPLAY = 5;

    const CALENDAR_DAY_STYLE = {
        UNSTYLED: '',
        ACTIVE_START: styles.selectedSquaredStart,
        ACTIVE_CONTINUED: styles.selectedSquaredContinued,
        ACTIVE_END: styles.selectedSquaredEnd,
        WITHDRAWAL_SINGLE: styles.selectedRoundedSingle,
        WITHDRAWAL_START: styles.selectedRoundedStart,
        WITHDRAWAL_CONTINUED: styles.selectedRoundedContinued,
        WITHDRAWAL_END: styles.selectedRoundedEnd,
    };

    const UNSTYLED_CALENDAR_WEEK_DATA = ['', '', '', '', '', '', ''];
    const INITIAL_CALENDAR_STYLE_DATA = [
        UNSTYLED_CALENDAR_WEEK_DATA,
        UNSTYLED_CALENDAR_WEEK_DATA,
        UNSTYLED_CALENDAR_WEEK_DATA,
        UNSTYLED_CALENDAR_WEEK_DATA,
        UNSTYLED_CALENDAR_WEEK_DATA,
    ];

    const advanceSelectionIndex = (
        previousDayStyle,
        dayIndex,
        goActiveIndex,
        numberOfDaysActive,
        numberOfDaysToWithdrawal,
        weekIndex,
        isSkipWeek
    ) => {
        const UNSTYLED = {
            selectedStyle: CALENDAR_DAY_STYLE.UNSTYLED,
            nextChangeIndex: goActiveIndex,
        };
        const START_SQUARE = {
            selectedStyle: CALENDAR_DAY_STYLE.ACTIVE_START,
            nextChangeIndex: dayIndex + 1,
        };
        const CONTINUE_SQUARE = {
            selectedStyle: CALENDAR_DAY_STYLE.ACTIVE_CONTINUED,
            nextChangeIndex: dayIndex + numberOfDaysActive - 2,
        };
        const END_SQUARE = {
            selectedStyle: CALENDAR_DAY_STYLE.ACTIVE_END,
            nextChangeIndex: dayIndex + 1,
        };
        const SINGLE_ROUND = {
            selectedStyle: CALENDAR_DAY_STYLE.WITHDRAWAL_SINGLE,
            nextChangeIndex: dayIndex + 1,
        };
        const START_ROUND = {
            selectedStyle: CALENDAR_DAY_STYLE.WITHDRAWAL_START,
            nextChangeIndex: dayIndex + 1,
        };
        const CONTINUE_ROUND = {
            selectedStyle: CALENDAR_DAY_STYLE.WITHDRAWAL_CONTINUED,
            nextChangeIndex: dayIndex + numberOfDaysToWithdrawal - 2,
        };
        const END_ROUND = {
            selectedStyle: CALENDAR_DAY_STYLE.WITHDRAWAL_END,
            nextChangeIndex: dayIndex + 1,
        };

        switch (previousDayStyle) {
            case CALENDAR_DAY_STYLE.ACTIVE_START:
                if (numberOfDaysActive > 2) {
                    return CONTINUE_SQUARE;
                }
                return END_SQUARE;
            case CALENDAR_DAY_STYLE.ACTIVE_CONTINUED:
                return END_SQUARE;
            case CALENDAR_DAY_STYLE.ACTIVE_END:
                if (numberOfDaysToWithdrawal === 0) {
                    return UNSTYLED;
                }
                if (numberOfDaysToWithdrawal === 1) return SINGLE_ROUND;
                return START_ROUND;
            case CALENDAR_DAY_STYLE.WITHDRAWAL_SINGLE:
                return UNSTYLED;
            case CALENDAR_DAY_STYLE.WITHDRAWAL_START:
                if (numberOfDaysToWithdrawal > 2) {
                    return CONTINUE_ROUND;
                }
                return END_ROUND;
            case CALENDAR_DAY_STYLE.WITHDRAWAL_CONTINUED:
                return END_ROUND;
            case CALENDAR_DAY_STYLE.WITHDRAWAL_END:
                if (
                    numberOfDaysActive + numberOfDaysToWithdrawal ===
                        WEEKDAYS_FULL.length &&
                    !isSkipWeek &&
                    (weekIndex + 1 < NUMBER_OF_WEEKS_TO_DISPLAY ||
                        goActiveIndex +
                            numberOfDaysActive +
                            numberOfDaysToWithdrawal <=
                            WEEKDAYS_FULL.length)
                ) {
                    return START_SQUARE;
                }
                return UNSTYLED;
            default: // UNSTYLED
                if (
                    weekIndex + 1 < NUMBER_OF_WEEKS_TO_DISPLAY ||
                    goActiveIndex +
                        numberOfDaysActive +
                        numberOfDaysToWithdrawal <=
                        WEEKDAYS_FULL.length
                ) {
                    return START_SQUARE;
                }
                return UNSTYLED;
        }
    };
    const shouldSkipWeek = (weekIndex, totalDaysLessThanWeek) => {
        switch (weekIndex) {
            case 0:
                return interval === INTERVAL.SECOND_AND_FOURTH_WEEK;
            case 1:
            case 3:
                return (
                    interval === INTERVAL.FIRST_AND_THIRD_WEEK &&
                    totalDaysLessThanWeek
                );
            case 2:
                return (
                    interval === INTERVAL.SECOND_AND_FOURTH_WEEK &&
                    totalDaysLessThanWeek
                );
            default:
                return (
                    interval !== INTERVAL.EVERY_WEEK && totalDaysLessThanWeek
                );
        }
    };
    const invalidCalendarPreviewOptions = () => {
        return (
            !dayToGoActive ||
            !(daysInActivePeriod >= 2 && daysInActivePeriod <= 7) ||
            !(
                (daysInWithdrawalPeriod > 0 || daysInWithdrawalPeriod === 0) &&
                daysInWithdrawalPeriod <= 7
            ) ||
            (interval === INTERVAL.EVERY_WEEK &&
                daysInWithdrawalPeriod + daysInActivePeriod >
                    WEEKDAYS_FULL.length) ||
            !(interval !== null && Object.values(INTERVAL).includes(interval))
        );
    };

    const updateCalendarPreview = () => {
        if (invalidCalendarPreviewOptions()) {
            return INITIAL_CALENDAR_STYLE_DATA;
        }
        const calendarData = [];

        let selectedStyle = CALENDAR_DAY_STYLE.UNSTYLED;
        const goActiveIndex = WEEKDAYS_FULL.indexOf(dayToGoActive);
        let nextChangeIndex = goActiveIndex;
        for (
            let weekIndex = 0;
            weekIndex < NUMBER_OF_WEEKS_TO_DISPLAY;
            weekIndex++
        ) {
            const isSkipWeek = shouldSkipWeek(
                weekIndex,
                daysInActivePeriod + daysInWithdrawalPeriod <=
                    WEEKDAYS_FULL.length
            );
            calendarData.push([]);
            for (
                let dayIndex = 0;
                dayIndex < WEEKDAYS_FULL.length;
                dayIndex++
            ) {
                const skipGoActive =
                    selectedStyle === CALENDAR_DAY_STYLE.UNSTYLED &&
                    dayIndex === goActiveIndex &&
                    isSkipWeek;
                if (dayIndex === nextChangeIndex && !skipGoActive) {
                    ({ selectedStyle, nextChangeIndex } = advanceSelectionIndex(
                        selectedStyle,
                        dayIndex,
                        goActiveIndex,
                        daysInActivePeriod,
                        daysInWithdrawalPeriod,
                        weekIndex,
                        isSkipWeek
                    ));
                    if (nextChangeIndex >= WEEKDAYS_FULL.length) {
                        nextChangeIndex -= WEEKDAYS_FULL.length;
                    }
                }
                calendarData[weekIndex].push(selectedStyle);
            }
        }
        return calendarData;
    };
    return (
        <Grid item className={styles.calendarPreview}>
            {updateCalendarPreview().map((week, index) => (
                <span className={styles.week} key={index}>
                    <span className={`${styles.day} ${week[0]}`}>Sun</span>
                    <span className={`${styles.day} ${week[1]}`}>Mon</span>
                    <span className={`${styles.day} ${week[2]}`}>Tue</span>
                    <span className={`${styles.day} ${week[3]}`}>Wed</span>
                    <span className={`${styles.day} ${week[4]}`}>Thr</span>
                    <span className={`${styles.day} ${week[5]}`}>Fri</span>
                    <span className={`${styles.day} ${week[6]}`}>Sat</span>
                </span>
            ))}
        </Grid>
    );
};

BidCycleCalendar.propTypes = {
    dayToGoActive: PropTypes.oneOf([
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        '',
    ]),
    daysInActivePeriod: PropTypes.number,
    daysInWithdrawalPeriod: PropTypes.number,
    interval: PropTypes.string,
};
